import type {
  CancelEmailChangeResponse,
  PendingEmailChangeResponse,
  RequestEmailChangeResponse,
} from '@shared/schemas/account';

import api from '@/shared/api/api-client';

import type { EmailChangeDraft, PendingEmailChange } from '../domain/email-change.model';
import type { EmailChangeGateway } from './email-change.gateway';

/**
 * Real implementation over the shared api client; the single place that knows the
 * endpoints and the snake_case bodies (`shared/schemas/account.ts`).
 *
 * The address is trimmed, the password is not. A leading or trailing space is a
 * legitimate character of a password, and dropping it here would compare something
 * the user never typed, so they would be told their password is wrong when it is
 * not. The backend trims the address too (`EmailIdentifier`), so doing it here only
 * keeps the request honest about what will be stored.
 *
 * `api.*` already unwraps the `{ success, data }` envelope, so what lands here is
 * the `data` object; `null` when nothing is pending.
 */
export const httpEmailChangeGateway: EmailChangeGateway = {
  requestChange: async (draft: EmailChangeDraft): Promise<PendingEmailChange> => {
    const data = await api.post<RequestEmailChangeResponse['data']>('/account/email', {
      current_password: draft.currentPassword,
      new_email: draft.newEmail.trim(),
    });
    return { pendingEmail: data.pending_email, expiresAt: data.expires_at };
  },

  loadPending: async (): Promise<PendingEmailChange | null> => {
    // `{ pending }`, not a bare nullable `data`: the api client unwraps with
    // `body.data ?? body`, so `data: null` would arrive here as the whole
    // `{ success, data }` envelope; truthy, and read as a live pending change with
    // an undefined address. See the note on `PendingEmailChangeResponseSchema`.
    const data = await api.get<PendingEmailChangeResponse['data']>('/account/email');
    const pending = data?.pending;
    if (!pending) return null;
    return { pendingEmail: pending.pending_email, expiresAt: pending.expires_at };
  },

  cancelPending: async (): Promise<{ cancelled: boolean }> => {
    const data = await api.delete<CancelEmailChangeResponse['data']>('/account/email');
    return { cancelled: data.cancelled };
  },
};
